import { gql, useQuery } from "@apollo/client";
import { getFetchPolicyOptions } from "api/utils";
import { useGetContractIdData } from "providers/ContractIdProvider";
import { useKeycloak } from "providers/KeycloakProvider";

const NEW_DOCUMENTS_CHECK = gql`
  query CheckNewDocuments($contactId: Long, $filterTags: [String]) {
    getLastLogins
    contact(id: $contactId) {
      documents(filterTags: $filterTags) {
        identifier
        lastModified
        fileName
      }
      portfolios {
        id
        documents(filterTags: $filterTags) {
          identifier
          lastModified
          fileName
        }
      }
    }
  }
`;

const filterTags: string[] = ["Online"];

interface DocumentsCheckQuery {
  getLastLogins: number[];
  contact: {
    documents: Document[];
    portfolios: {
      id: number;
      documents: Document[];
    }[];
  };
}

export const useCheckForNewDocuments = () => {
  const { selectedContactId } = useGetContractIdData();
  const { loading, error, data } = useQuery<DocumentsCheckQuery>(NEW_DOCUMENTS_CHECK, {
    variables: {
      contactId: selectedContactId,
      filterTags,
    },
    skip: !selectedContactId,
    fetchPolicy: "cache-and-network",
  });

  return {
    loading,
    error,
    data:
      data && selectedContactId
        ? latestNewDocumentTime(
            [...data.contact.documents, ...data.contact.portfolios.flatMap((p) => p.documents)],
            data.getLastLogins
          )
        : false,
  };
};

/** Returns the latest lastModified timestamp (ms) of documents newer than the previous login, or false if none. */
const latestNewDocumentTime = (documents: Document[], lastLogins: number[]): number | false => {
  const sorted = [...lastLogins].sort((a, b) => b - a);
  const previousLogin = sorted[1];
  if (previousLogin === undefined) return false;
  const newDocs = documents.filter((doc) => new Date(doc.lastModified).getTime() > previousLogin);
  if (newDocs.length === 0) return false;
  return Math.max(...newDocs.map((doc) => new Date(doc.lastModified).getTime()));
};

export interface Document {
  identifier: string;
  fileName: string;
  lastModified: string;
}
